import ReactDOM from 'react-dom';
import Modal from '../../../components/Modal';
import { tosThumbnail } from '../../../utils/tosImage';
import { Lightbox } from './AnalysisElements';
import { SearchSelectGroup, SearchSelectModalState } from '../utils/assetSearchDerived';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface SearchSelectModalProps {
  modal: SearchSelectModalState | null;
  groups: SearchSelectGroup[];
  selectedUrls: string[];
  saving: boolean;
  lightboxIndex: number | null;
  images: string[];
  t: Translate;
  onClose: () => void;
  onClearSelection: () => void;
  onConfirm: () => void;
  onSelectedUrlsChange: React.Dispatch<React.SetStateAction<string[]>>;
  onLightboxIndexChange: React.Dispatch<React.SetStateAction<number | null>>;
  onRetry: (group: SearchSelectGroup) => void;
  onManualSearch: (group: SearchSelectGroup) => void;
}

export default function SearchSelectModal({
  modal,
  groups,
  selectedUrls,
  saving,
  lightboxIndex,
  images,
  t,
  onClose,
  onClearSelection,
  onConfirm,
  onSelectedUrlsChange,
  onLightboxIndexChange,
  onRetry,
  onManualSearch,
}: SearchSelectModalProps) {
  if (!modal) return null;

  const maxSelectable = Math.max(0, modal.maxTotal - modal.currentRefCount);
  const completedCount = groups.filter(group => group.status === 'completed' || group.status === 'failed').length;
  const totalCount = groups.length;
  const modalTitle = modal.state && modal.state !== '__base__'
    ? t('searchModal.titleWithState', { name: modal.elementName, state: modal.state })
    : t('searchModal.title', { name: modal.elementName });

  return (
    <Modal
      open
      onClose={onClose}
      title={modalTitle}
      width="800px"
      footer={
        <>
          <button
            onClick={onClearSelection}
            className="btn btn-ghost"
            style={{ marginRight: 'auto', fontSize: '0.8rem', color: 'var(--text-3)' }}
            disabled={selectedUrls.length === 0}
          >
            {t('searchModal.clearSelection')}
          </button>
          <button onClick={onClose} className="btn btn-secondary">
            {t('cancel', { ns: 'common' })}
          </button>
          <button
            onClick={onConfirm}
            disabled={selectedUrls.length === 0 || saving}
            className="btn btn-primary"
          >
            {saving ? t('saving', { ns: 'common' }) : t('searchModal.confirmAdd', { count: selectedUrls.length })}
          </button>
        </>
      }
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '0.75rem', fontSize: '0.8rem', color: 'var(--text-3)',
      }}>
        <span>
          {t('searchModal.currentRefs', { current: modal.currentRefCount, max: modal.maxTotal })}
          {maxSelectable > 0 ? t('searchModal.canSelectMore', { count: maxSelectable }) : t('searchModal.maxReached')}
          {completedCount < totalCount && (
            <span style={{ marginLeft: '0.5rem', color: 'var(--warning, #e6a23c)' }}>
              {t('searchModal.searchProgress', { completed: completedCount, total: totalCount })}
            </span>
          )}
        </span>
        <span style={{
          padding: '2px 10px', borderRadius: '10px', fontSize: '0.75rem',
          background: selectedUrls.length > 0 ? 'var(--primary)' : 'var(--bg-2)',
          color: selectedUrls.length > 0 ? 'white' : 'var(--text-3)',
          transition: 'all 0.2s',
        }}>
          {t('searchModal.selected', { current: selectedUrls.length, max: maxSelectable })}
        </span>
      </div>
      <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '4px' }}>
        {groups.map((group, groupIndex) => {
          const groupStartIndex = groups.slice(0, groupIndex).reduce((sum, item) => sum + item.images.length, 0);
          const isPending = group.status === 'pending';
          const isSearching = group.status === 'queued' || group.status === 'searching';
          const isGenerating = group.status === 'generating_keywords';
          const isFailed = group.status === 'failed';
          return (
            <div key={groupIndex} style={{ marginBottom: groupIndex < groups.length - 1 ? '1.25rem' : 0 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                marginBottom: '0.5rem', paddingBottom: '0.35rem',
                borderBottom: '1px solid var(--border)',
              }}>
                <span style={{
                  fontSize: '0.7rem', color: 'white',
                  background: isFailed ? 'var(--danger, #f56c6c)' : isSearching || isGenerating ? 'var(--warning, #e6a23c)' : isPending ? 'var(--text-3, #999)' : 'var(--primary)',
                  borderRadius: '3px', padding: '1px 6px', fontWeight: 600, flexShrink: 0,
                }}>
                  {groupIndex + 1}
                </span>
                {group.state && (
                  <span style={{
                    fontSize: '0.7rem', color: 'var(--primary)', background: 'var(--primary-light)',
                    borderRadius: '3px', padding: '1px 6px', fontWeight: 500, flexShrink: 0,
                    border: '1px solid var(--primary)',
                  }}>
                    {group.state}
                  </span>
                )}
                <span style={{
                  fontSize: '0.8rem', color: 'var(--text-2)', fontStyle: 'italic',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  flex: 1, minWidth: 0,
                }}>
                  {group.keywords || '-'}
                </span>
                {group.taskId && (
                  <span
                    title={t('searchModal.copyTaskId')}
                    onClick={(event) => {
                      event.stopPropagation();
                      navigator.clipboard.writeText(group.taskId);
                      const el = event.currentTarget;
                      el.style.color = 'var(--success, #67c23a)';
                      setTimeout(() => { el.style.color = ''; }, 1000);
                    }}
                    style={{
                      fontSize: '0.65rem', color: 'var(--text-4, #aaa)', flexShrink: 0,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '2px',
                      fontFamily: 'monospace', userSelect: 'none',
                    }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                    {group.taskId.length > 8 ? group.taskId.slice(-8) : group.taskId}
                  </span>
                )}
                {group.status === 'completed' && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-3)', flexShrink: 0 }}>
                    {t('searchModal.nImages', { count: group.images.length })}
                  </span>
                )}
                {group.status === 'completed' && group.images.length === 0 && group.retryCount < 3 && (
                  <button
                    onClick={(event) => { event.stopPropagation(); onRetry(group); }}
                    style={{
                      fontSize: '0.68rem', color: 'var(--warning, #e6a23c)', flexShrink: 0,
                      display: 'flex', alignItems: 'center', gap: '3px',
                      background: 'none', border: '1px solid var(--warning, #e6a23c)',
                      borderRadius: '3px', padding: '1px 6px', cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    title={t('searchModal.retryTitle', { current: group.retryCount, max: 3 })}
                  >
                    {t('searchModal.retryN', { current: group.retryCount, max: 3 })}
                  </button>
                )}
                {group.status === 'completed' && group.images.length === 0 && group.retryCount >= 3 && (
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-4, #ccc)', flexShrink: 0 }}>
                    {t('searchModal.retryLimit')}
                  </span>
                )}
                {isPending && (
                  <button
                    onClick={(event) => { event.stopPropagation(); onManualSearch(group); }}
                    style={{
                      fontSize: '0.68rem', color: 'var(--primary)', flexShrink: 0,
                      display: 'flex', alignItems: 'center', gap: '3px',
                      background: 'none', border: '1px solid var(--primary)',
                      borderRadius: '3px', padding: '1px 6px', cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    title={t('searchModal.manualSearchTitle')}
                  >
                    {t('searchModal.searchBtn')}
                  </button>
                )}
                {group.status === 'queued' && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-3, #999)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {t('searchModal.queuedStatus')}
                  </span>
                )}
                {group.status === 'searching' && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--warning, #e6a23c)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', border: '2px solid var(--warning, #e6a23c)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    {t('searchModal.searchingStatus')}
                  </span>
                )}
                {isGenerating && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--warning, #e6a23c)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', border: '2px solid var(--warning, #e6a23c)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    {t('searchModal.generatingKeywords')}
                  </span>
                )}
                {isFailed && (
                  <>
                    <span style={{ fontSize: '0.72rem', color: 'var(--danger, #f56c6c)', flexShrink: 0 }}>
                      {t('searchModal.searchFailed')}
                    </span>
                    {group.retryCount < 3 ? (
                      <button
                        onClick={(event) => { event.stopPropagation(); onRetry(group); }}
                        style={{
                          fontSize: '0.68rem', color: 'var(--warning, #e6a23c)', flexShrink: 0,
                          display: 'flex', alignItems: 'center', gap: '3px',
                          background: 'none', border: '1px solid var(--warning, #e6a23c)',
                          borderRadius: '3px', padding: '1px 6px', cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                        title={t('searchModal.retryTitle', { current: group.retryCount, max: 3 })}
                      >
                        {t('searchModal.retryN', { current: group.retryCount, max: 3 })}
                      </button>
                    ) : (
                      <span style={{ fontSize: '0.68rem', color: 'var(--text-4, #ccc)', flexShrink: 0 }}>
                        {t('searchModal.retryLimit')}
                      </span>
                    )}
                  </>
                )}
                {group.status === 'cancelled' && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-3, #999)', flexShrink: 0 }}>
                    {t('searchModal.cancelled')}
                  </span>
                )}
              </div>
              {group.status === 'completed' && group.images.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '0.6rem',
                }}>
                  {group.images.map((url, imageIndex) => {
                    const flatIndex = groupStartIndex + imageIndex;
                    const isSelected = selectedUrls.includes(url);
                    const maxReached = selectedUrls.length >= maxSelectable;
                    const isDisabled = !isSelected && maxReached;
                    return (
                      <div key={imageIndex} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <div
                          onClick={() => {
                            if (isDisabled) return;
                            onSelectedUrlsChange(previous =>
                              isSelected ? previous.filter(item => item !== url) : [...previous, url]
                            );
                          }}
                          style={{
                            position: 'relative',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            borderRadius: '8px 8px 0 0',
                            overflow: 'hidden',
                            opacity: isDisabled ? 0.35 : 1,
                            border: isSelected ? '2.5px solid var(--primary)' : '2.5px solid var(--border)',
                            borderBottom: 'none',
                            boxShadow: isSelected ? '0 0 0 1px var(--primary), 0 2px 8px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
                            transition: 'all 0.15s ease',
                            transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                            background: 'var(--bg-1)',
                          }}
                        >
                          <img
                            src={tosThumbnail(url, 220, 140)}
                            alt={t('searchModal.searchResult', { n: flatIndex + 1 })}
                            loading="lazy"
                            style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block' }}
                          />
                          {isSelected && (
                            <div style={{
                              position: 'absolute', top: '6px', right: '6px',
                              width: '22px', height: '22px', borderRadius: '50%',
                              background: 'var(--primary)', color: 'white',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '13px', fontWeight: 700,
                              boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                            }}>
                              {selectedUrls.indexOf(url) + 1}
                            </div>
                          )}
                          {!isSelected && !isDisabled && (
                            <div style={{
                              position: 'absolute', top: '6px', right: '6px',
                              width: '22px', height: '22px', borderRadius: '50%',
                              background: 'rgba(0,0,0,0.3)', border: '2px solid rgba(255,255,255,0.7)',
                            }} />
                          )}
                        </div>
                        <button
                          onClick={(event) => { event.stopPropagation(); onLightboxIndexChange(flatIndex); }}
                          style={{
                            border: isSelected ? '2.5px solid var(--primary)' : '2.5px solid var(--border)',
                            borderTop: `1px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                            borderRadius: '0 0 8px 8px',
                            background: 'var(--bg-2)',
                            padding: '4px 0',
                            fontSize: '0.72rem',
                            color: 'var(--text-3)',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                            opacity: isDisabled ? 0.35 : 1,
                          }}
                        >
                          {t('elementCard.showLargeImage')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {group.status === 'completed' && group.images.length === 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', padding: '0.5rem 0' }}>
                  {t('searchModal.noResults')}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {lightboxIndex !== null && ReactDOM.createPortal(
        <Lightbox
          urls={images}
          index={lightboxIndex}
          onClose={() => onLightboxIndexChange(null)}
          onChange={onLightboxIndexChange}
          selectedUrls={selectedUrls}
          maxSelectable={maxSelectable}
          onMultiSelect={(url) => {
            onSelectedUrlsChange(previous =>
              previous.includes(url) ? previous.filter(item => item !== url) : previous.length < maxSelectable ? [...previous, url] : previous
            );
          }}
        />,
        document.body,
      )}
    </Modal>
  );
}
